import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Download, TrendingUp, Sparkles, Wallet } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Récap Comptable Wave Client — PDF premium
const ClientWaveRecapDialog = ({ open, onOpenChange, client }) => {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const now = useMemo(() => new Date(), []);
  const currentPeriod = useMemo(() => {
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }, [now]);

  const [period, setPeriod] = useState(currentPeriod);
  const [isRange, setIsRange] = useState(false);
  const [toPeriod, setToPeriod] = useState(currentPeriod);

  const displayName = client?.companyName ||
    `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || 'Client';

  const handleGenerate = async () => {
    if (!client?.id) return;
    if (isRange && toPeriod < period) {
      toast({
        title: 'Plage invalide',
        description: 'La date de fin doit être postérieure ou égale à la date de début.',
        variant: 'destructive',
      });
      return;
    }
    setDownloading(true);
    try {
      const params = { period };
      if (isRange && toPeriod !== period) params.to_period = toPeriod;
      const res = await axios.get(
        `${API}/wave/clients/${client.id}/recap/download`,
        { params, responseType: 'blob' },
      );
      // Guard: if server returned JSON error with blob wrapper, parse it
      const ct = res.headers?.['content-type'] || '';
      if (!ct.includes('application/pdf')) {
        try {
          const text = await res.data.text();
          const j = JSON.parse(text);
          throw new Error(j?.detail || 'Réponse serveur invalide');
        } catch (parseErr) {
          throw new Error(parseErr.message || 'Le serveur n\'a pas renvoyé un PDF');
        }
      }
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const label = isRange && toPeriod !== period ? `${period}_to_${toPeriod}` : period;
      link.download = `recap-wave-${client.clientNumber || client.id.slice(0, 8)}-${label}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Récap généré', description: 'Le PDF a été téléchargé.' });
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.status === 404
        ? 'Client introuvable ou aucune donnée pour cette période.'
        : (e?.response?.data?.detail || 'Impossible de générer le récap.');
      toast({ title: 'Erreur', description: msg, variant: 'destructive' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="wave-recap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Récap Comptable Wave
            <span className="ml-1 inline-block px-2 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold tracking-wide">
              PREMIUM
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Génère un PDF client détaillant la marge réelle après impact des frais Wave — livrable
            facturable pour <strong>{displayName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900 flex items-start gap-2">
            <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
            <div>
              Le PDF inclut : marge brute vs marge réelle, tendance sur 3 mois glissants,
              détail opération par opération et impact Wave % du CA. Idéal pour justifier
              une révision tarifaire.
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium">
              {isRange ? 'Mois de début' : 'Mois du récap'}
            </Label>
            <Input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-9 text-sm mt-1"
              data-testid="recap-period-input"
              max={currentPeriod}
            />
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox
              checked={isRange}
              onCheckedChange={(v) => setIsRange(!!v)}
              data-testid="recap-range-toggle"
            />
            <span>Étendre sur une plage (récap trimestriel ou annuel)</span>
          </label>

          {isRange && (
            <div>
              <Label className="text-xs font-medium">Mois de fin (inclus)</Label>
              <Input
                type="month"
                value={toPeriod}
                onChange={(e) => setToPeriod(e.target.value)}
                className="h-9 text-sm mt-1"
                data-testid="recap-toperiod-input"
                min={period}
                max={currentPeriod}
              />
            </div>
          )}

          <div className="text-[10px] text-slate-500 italic flex items-center gap-1">
            <Wallet className="w-3 h-3" />
            Seules les opérations terminées / livrées / clôturées sont incluses (cohérence comptable).
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={downloading}
            data-testid="recap-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={downloading || !period}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="recap-download-btn"
          >
            {downloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Télécharger le PDF</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClientWaveRecapDialog;
